import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ShopifyProduct } from '@/lib/shopify';
import { createStorefrontCheckout } from '@/lib/shopify';

export interface CartItem {
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  quantity: number;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  customDesignId?: string; // For custom AI designs
}

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isDemoMode: boolean;
  
  addItem: (item: CartItem) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  removeItem: (variantId: string) => void;
  clearCart: () => void;
  setCartId: (cartId: string) => void;
  setCheckoutUrl: (url: string) => void;
  setLoading: (loading: boolean) => void;
  setDemoMode: (demoMode: boolean) => void;
  createCheckout: () => Promise<void>;
  trackCheckoutAttempt: () => Promise<void>;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isDemoMode: true, // Enable demo mode by default

      addItem: (item) => {
        const { items } = get();
        const existingItem = items.find(i => i.variantId === item.variantId);
        
        if (existingItem) {
          set({
            items: items.map(i =>
              i.variantId === item.variantId
                ? { ...i, quantity: i.quantity + item.quantity }
                : i
            )
          });
        } else {
          set({ items: [...items, item] });
        }
      },

      updateQuantity: (variantId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(variantId);
          return;
        }
        
        set({
          items: get().items.map(item =>
            item.variantId === variantId ? { ...item, quantity } : item
          )
        });
      },

      removeItem: (variantId) => {
        set({
          items: get().items.filter(item => item.variantId !== variantId)
        });
      },

      clearCart: () => {
        set({ items: [], cartId: null, checkoutUrl: null });
      },

      setCartId: (cartId) => set({ cartId }),
      setCheckoutUrl: (checkoutUrl) => set({ checkoutUrl }),
      setLoading: (isLoading) => set({ isLoading }),
      setDemoMode: (isDemoMode) => set({ isDemoMode }),

      trackCheckoutAttempt: async () => {
        const { items } = get();
        if (items.length === 0) return;

        const totalAmount = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);
        const currencyCode = items[0]?.price.currencyCode || 'USD';

        try {
          const { supabase } = await import('@/integrations/supabase/client');
          
          // Generate a session ID for rate limiting (stored in localStorage)
          let sessionId = localStorage.getItem('checkout_session_id');
          if (!sessionId) {
            sessionId = crypto.randomUUID();
            localStorage.setItem('checkout_session_id', sessionId);
          }

          const { data, error } = await supabase.functions.invoke('track-checkout-attempt', {
            body: {
              cart_items: items,
              total_amount: totalAmount,
              currency_code: currencyCode,
              session_id: sessionId,
            }
          });

          if (error) {
            if (import.meta.env.DEV) {
              console.error('Failed to track checkout attempt:', error);
            }
          }
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('Failed to track checkout attempt:', error);
          }
        }
      },

      createCheckout: async () => {
        const { items, setLoading, setCheckoutUrl } = get();
        if (items.length === 0) return;

        setLoading(true);
        try {
          const checkoutUrl = await createStorefrontCheckout(items);
          setCheckoutUrl(checkoutUrl);
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('Failed to create checkout:', error);
          }
          throw error;
        } finally {
          setLoading(false);
        }
      }
    }),
    {
      name: 'shopify-cart',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
